import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Transition } from '@headlessui/react'
import { clsx } from 'clsx'

import { Backdrop } from '../Backdrop/index.js'
import { BackgroundProvider } from '../BackgroundContext/index.js'
import { useDocument } from '../hooks/useDocument.js'
import { Portal } from '../Portal/index.js'
import {
  nodeContainsDescendant,
  wasContentNodeDescendant,
} from '../utils/descendant.js'
import { focusFirstFocusableNode } from '../utils/focus.js'
import { useUniqueId } from '../utils/unique-id.js'
import { useKeypressListener } from '../utils/useKeypressListener.js'
import { ModalPanel } from './components/ModalPanel.js'
import { ModalContext } from './ModalContext.js'
import { ModalActions, ModalFooter } from './ModalFooter.js'
import { ModalHeader } from './ModalHeader.js'
import { ModalProps } from './types.js'
import { labelledById } from './utils.js'

/**
 * A dialog is a window overlaid on either the primary window or another dialog window.
 * Content behind a modal dialog is inert, meaning that users cannot interact with it.
 */
export const Modal = ({
  open = false,
  onClose,
  children,
  size = 'lg',
  className,
  initialFocus,
  closeOnBackdropClick = true,
  activator,
  showCloseBtn = true,
  showZenModeBtn = false,
}: ModalProps) => {
  const { document } = useDocument()
  const id = useUniqueId('modal')

  const activatorRef = useRef<HTMLDivElement>(null)
  const containerNode = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (
      containerNode.current &&
      !containerNode.current.contains(document.activeElement)
    ) {
      if (initialFocus && initialFocus.current) {
        initialFocus.current.focus()
      } else {
        focusFirstFocusableNode(document, containerNode.current)
      }
    }
  }, [document, initialFocus])

  const [zenMode, setZenMode] = useState(false)
  const toggleZenMode = useCallback(() => {
    setZenMode(zenMode => !zenMode)
  }, [])

  const handleClose = useCallback(() => {
    onClose()

    const activatorElement =
      activator && isRef(activator)
        ? activator && activator.current
        : activatorRef.current
    if (activatorElement) {
      requestAnimationFrame(() =>
        focusFirstFocusableNode(document, activatorElement),
      )
    }
  }, [document, activator, onClose])

  const activatorMarkup =
    activator && !isRef(activator) ? (
      <div ref={activatorRef}>{activator}</div>
    ) : null

  const handleEscape = (event: Event) => {
    if (!open) {
      return
    }

    const target = event.target as HTMLElement
    const composedPath = event.composedPath()
    const wasDescendant = wasContentNodeDescendant(composedPath, containerNode)
    const isActivatorDescendant = nodeContainsDescendant(
      activatorRef.current,
      target,
    )

    if (wasDescendant || isActivatorDescendant) {
      handleClose()
    }
  }

  const handleBackdropClick = () => {
    if (!closeOnBackdropClick) {
      return
    }
    handleClose()
  }

  const contextValue = useMemo(
    () => ({
      id,
      open,
      onClose: handleClose,
      showCloseBtn,
      showZenModeBtn,
      zenMode,
      toggleZenMode,
    }),
    [
      id,
      open,
      handleClose,
      showCloseBtn,
      showZenModeBtn,
      zenMode,
      toggleZenMode,
    ],
  )

  useKeypressListener('Escape', handleEscape, 'keyup')

  return (
    <>
      {activatorMarkup}
      <Portal idPrefix="modal">
        <ModalContext.Provider value={contextValue}>
          <BackgroundProvider backgroundType="surface">
            <Transition appear show={open}>
              <div
                className={clsx(
                  'pointer-events-none fixed inset-0 z-10 flex flex-col justify-center',
                  'overflow-y-hidden overscroll-contain',
                  'transition-all',
                  !zenMode && 'p-0 sm:p-4',
                  className,
                )}
              >
                <Transition.Child
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  className="min-h-0"
                >
                  <ModalPanel
                    ref={containerNode}
                    aria-labelledby={labelledById(id)}
                    size={size}
                    zenMode={zenMode}
                  >
                    {children}
                  </ModalPanel>
                </Transition.Child>
              </div>

              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Backdrop onClick={handleBackdropClick} />
              </Transition.Child>
            </Transition>
          </BackgroundProvider>
        </ModalContext.Provider>
      </Portal>
    </>
  )
}

export type ModalContentProps = ComponentProps<'div'> & {
  padding?: boolean
}

const ModalContent: FC<ModalContentProps> = props => {
  const { children, className, padding = true, ...rest } = props

  return (
    <div
      className={clsx('flex-1', padding && 'px-4 py-5 sm:p-6', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

Modal.Header = ModalHeader
Modal.Content = ModalContent
Modal.Footer = ModalFooter
Modal.Actions = ModalActions

function isRef(
  ref: React.RefObject<HTMLElement> | React.ReactElement,
): ref is React.RefObject<HTMLElement> {
  return Object.prototype.hasOwnProperty.call(ref, 'current')
}
